import { Button, ConfirmModal, FilterSearchBar, Table } from '@/components';
import { useInvalidateBusinessAreaQueries } from '@/modules/BusinessAreas/hooks/use-invalidate-business-areas-queries';
import { AttachUserToBusinessModal } from '@/modules/BusinessAreas/pages/BusinessAreasPage/components/AttachUserToBusinessModal';
import { CreateBusinessAreaModal } from '@/modules/BusinessAreas/pages/BusinessAreasPage/components/CreateBusinessAreaModal';
import { useDeleteBusinessArea } from '@/modules/BusinessAreas/queries';
import {
  automationsCountColumnConfig,
  codeColumnConfig,
  companyNameColumnConfig,
  deleteRowColumnConfig,
  employeesCountColumnConfig,
  getBusinessAreasChildRows,
  nameWithAvatarColumnConfig,
  processesCountColumnConfig,
  statusColumnConfig,
  tasksCountColumnConfig,
  workflowsColumnConfig,
} from '@/utils';
import {
  ReactElement,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { TabContext } from '../TabContainer';
import { TableRow } from '../Table/types';

interface BusinessAreasTabProps {
  canAttachUser?: boolean;
  canCreate?: boolean;
  userId?: number;
  disableFields?: string[];
  organizationId?: number;
  companyId?: number;
}

export const BusinessAreasTab = ({
  canAttachUser,
  canCreate,
  userId,
  disableFields,
  organizationId,
  companyId,
}: BusinessAreasTabProps): ReactElement => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const invalidateBusinessAreaQueries = useInvalidateBusinessAreaQueries();

  const [businessAreaSelected, setBusinessAreaSelected] = useState<number>();
  const [isOpenDelete, setIsOpenDelete] = useState(false);

  const handleOpenRemoveBusinessModalState = useCallback((id: number) => {
    setIsOpenDelete(true);
    setBusinessAreaSelected(id);
  }, []);

  const routeChange = (row?: TableRow) => {
    const path = `/business-areas/${row?.id}`;
    navigate(path);
  };

  const handleCloseRemoveBusinessAreaModalState = useCallback(() => {
    setIsOpenDelete(false);
    setBusinessAreaSelected(undefined);
  }, []);

  const { mutate: deleteBusinessArea } = useDeleteBusinessArea();

  const {
    data,
    isLoading,
    isOpen,
    page,
    onPageChange,
    handleSearchChange,
    handleModalState,
    onSort,
  } = useContext(TabContext);

  const columns = [
    nameWithAvatarColumnConfig({}),
    codeColumnConfig(),
    statusColumnConfig(),
    companyNameColumnConfig(),
    employeesCountColumnConfig(),
    processesCountColumnConfig(),
    workflowsColumnConfig(),
    tasksCountColumnConfig(),
    automationsCountColumnConfig({
      options: {
        className: 'w-25',
      },
    }),
    deleteRowColumnConfig({
      onDeleteClick: (row) =>
        handleOpenRemoveBusinessModalState(row.id as number),
      disabledMessage: t('delete_ba'),
    }),
  ];

  const rows = useMemo(
    () => getBusinessAreasChildRows(data?.items ?? []),
    [data],
  );

  const handleDeleteBusinessArea = useCallback(() => {
    if (!businessAreaSelected) {
      return;
    }

    deleteBusinessArea(businessAreaSelected, {
      onError: () => {
        toast.error(t('error_deleting_ba'));
      },
      onSuccess: () => {
        toast.success(t('success_deleting_ba'));
        invalidateBusinessAreaQueries();
      },
    });

    handleCloseRemoveBusinessAreaModalState();
  }, [
    businessAreaSelected,
    deleteBusinessArea,
    handleCloseRemoveBusinessAreaModalState,
    invalidateBusinessAreaQueries,
    t,
  ]);

  return (
    <>
      <div className="center flex flex-row">
        <FilterSearchBar
          total={data?.totalBusinessAreas || 0}
          title={t('business_areas')}
          onSearch={handleSearchChange}
          variant={'secondary'}
          otherActions={
            <>
              {canCreate && (
                <Button onClick={handleModalState} variant="primary">
                  {t('add')}
                </Button>
              )}
              {canAttachUser && userId && (
                <AttachUserToBusinessModal userId={userId} />
              )}
            </>
          }
        />
      </div>
      <CreateBusinessAreaModal
        handleClose={handleModalState}
        isOpen={isOpen}
        disableFields={disableFields}
        organizationId={organizationId}
        companyId={companyId}
      />
      <Table
        onRowClick={routeChange}
        columns={columns}
        rows={rows}
        isLoading={isLoading}
        noResultsLabel={t('no_business_areas_available')}
        withPagination
        totalItems={data?.totalRows}
        totalPages={data?.totalPages}
        currentPage={page}
        onPageChange={onPageChange}
        onSort={onSort}
        isTree
      />
      <ConfirmModal
        isOpen={isOpenDelete}
        handleClose={handleCloseRemoveBusinessAreaModalState}
        title={t('please_confirm_action')}
        message={t('confirm_business_area_delete')}
        onConfirm={handleDeleteBusinessArea}
      />
    </>
  );
};
